import React, { useState, useRef, useEffect } from "react";
import { Button } from "react-bootstrap";
import './AttachImageOption.css'
import { BsPaperclip, BsX } from "react-icons/bs";

interface Props {
    uploadedImages?: File[];
    setUploadedImages: (uploads: File[]) => React.Dispatch<React.SetStateAction<File[]>>;
};

const AttachImageOption = ({ uploadedImages = [], setUploadedImages }: Props) => {

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [uploadError, setUploadError] = useState('');

    useEffect(() => {
        if (uploadError) {
            const timer = setTimeout(() => setUploadError(''), 3000);
            return () => clearTimeout(timer);
        }
    }, [uploadError]);

    const handleUploadClick = () => {
        fileInputRef?.current?.click();
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        let newImages = [...uploadedImages];
        let totalSize = newImages.reduce((sum, file) => sum + file.size, 0);

        for (let file of files) {
            const imageFile = file as File;
            if (!imageFile.type.startsWith('image/')) continue;
            if (newImages.length >= 3) {
                setUploadError('You can only upload up to 3 images.');
                return;
            }
            if (totalSize + imageFile.size > 5 * 1024 * 1024) {
                setUploadError('Total file size must not exceed 5MB.');
                return;
            }

            newImages.push(imageFile);
            totalSize += imageFile.size;
        }

        setUploadedImages(newImages);
        setUploadError('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }

    };

    const handleRemoveImage = (index: number) => {
        const updated = [...uploadedImages];
        updated.splice(index, 1);
        setUploadedImages(updated);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    margin: '1rem 0 0.5rem',
                }}
            >
                <span className="attach-text">
                    Attach an image <span style={{ fontWeight: 400 }}>(optional)</span>
                </span>
                <Button
                    className="button-accent-light"
                    onClick={handleUploadClick}
                    onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.03)')}
                    onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                >
                    <div className="d-flex align-items-center text-nowrap gap-1">
                        <BsPaperclip size={16} />
                        Upload
                    </div>
                </Button>

                <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageChange}
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                />
            </div>

            <div className="upload-error">{uploadError}</div>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
                {uploadedImages.map((file, index) => (
                    <div key={index} style={{ position: 'relative' }}>
                        <img
                            className="uploaded-image"
                            src={URL.createObjectURL(file)}
                            alt={`upload-${index}`}
                        />
                        <BsX
                            className="remove-image-button"
                            onClick={() => handleRemoveImage(index)}
                        />
                    </div>
                ))}
            </div>
        </>
    );
};

export default AttachImageOption;